// src/contexts/PermissionsContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const [canEdit, setCanEdit] = useState(true);
  const [canExecuteCommand, setCanExecuteCommand] = useState(true);

  const setPermissions = useCallback(({ canEdit: edit, canExecuteCommand: execute }) => {
    setCanEdit(typeof edit === "boolean" ? edit : true);
    setCanExecuteCommand(typeof execute === "boolean" ? execute : true);

    console.info("[PermissionsProvider] Permissões aplicadas", {
      canEdit: typeof edit === "boolean" ? edit : true,
      canExecuteCommand: typeof execute === "boolean" ? execute : true,
    });
  }, []);

  const resetPermissions = useCallback(() => {
    setCanEdit(true);
    setCanExecuteCommand(true);
  }, []);

  useDisableSaveButtons(canEdit);

  return (
    <PermissionsContext.Provider
      value={{
        canEdit,
        canExecuteCommand,
        setPermissions,
        resetPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

const SAVE_ACTION_TERMS = [
  "salvar",
  "Salvar",
  "registrar",
  "registrando",
  "salvar novo",
  "salvando",
  "inserir",
  "redefinir",
  "inserindo",
  "atualizar área",
  "importar",
  "import",
  "save",
  "saving",
  "atualizar",
  "incluir",
  "incluindo",
  "atualizando",
  "update",
  "updating",
  "excluir",
  "apagar",
  "apagando",
  "excluindo",
  "deletar",
  "deletando",
  "remover",
  "removendo",
  "ok",
  "confirmar",
  "confirmando",
  "concluir",
  "concluindo",
];

function useDisableSaveButtons(canEdit) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const selector = [
      "button",
      "a",
      "input[type='button']",
      "input[type='submit']",
      "[role='button']",
    ].join(",");

    const isSaveButton = (element) => {
      const text = [
        element.textContent,
        element.value,
        element.getAttribute("title"),
        element.getAttribute("aria-label"),
        element.getAttribute("name"),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return SAVE_ACTION_TERMS.some((term) => text.includes(term));
    };

    const applyRule = () => {
      document.querySelectorAll(selector).forEach((element) => {
        const guarded = element.getAttribute("data-can-edit-guarded") === "true";

        if (canEdit) {
          if (guarded) {
            const wasDisabled = element.getAttribute("data-original-disabled") === "true";

            if (!wasDisabled) {
              element.removeAttribute("disabled");
              element.removeAttribute("aria-disabled");
            }

            element.style.pointerEvents = "";
            element.removeAttribute("data-can-edit-guarded");
            element.removeAttribute("data-original-disabled");
          }
          return;
        }

        if (!isSaveButton(element)) return;

        if (!guarded) {
          element.setAttribute(
            "data-original-disabled",
            element.hasAttribute("disabled") ? "true" : "false"
          );
        }

        if (!element.hasAttribute("disabled")) {
          element.setAttribute("disabled", "true");
        }
        if (element.getAttribute("aria-disabled") !== "true") {
          element.setAttribute("aria-disabled", "true");
        }
        if (element.style.pointerEvents !== "none") {
          element.style.pointerEvents = "none";
        }
        if (!guarded) {
          element.setAttribute("data-can-edit-guarded", "true");
        }
      });
    };

    applyRule();

    const observer = new MutationObserver(applyRule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-label", "title", "value"],
    });

    return () => observer.disconnect();
  }, [canEdit]);
}
